import { useEffect, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../../../store/store';
import { fetchUsers, updateUserStatus, UserData } from '../../../../store/AdminUsersSlice';
import { showAlert } from '../../../../store/CommonSlice';
import { AlertType } from '../../../../types/common';
import Content from '../../../layouts/adminLayouts/Content/Content';
import Actions from '../../../layouts/adminLayouts/Actions/Actions';
import Button from '../../../UI/Button/Button';
import Loader from '../../../UI/Loader/Loader';
import Placeholder from '../../../UI/Placeholder/Placeholder';
import { NO_USERS } from '../../../../constants/messages';
import Card from '../../../UI/Card/Card';
import User from '../../../admin/User/User';
import styles from './UsersPage.module.css';
import { exportUsersToExcel } from '../../../../utils/exportUsersToExcel';

const getTierName = (tier?: string | null): string => {
    const names: Record<string, string> = {
        base: 'бронзовый',
        silver: 'серебряный',
        gold: 'золотой',
        platinum: 'VIP',
    };
    return tier ? names[tier] || tier : '-';
};

const UsersPage: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const users = useSelector((state: RootState) => state.adminUsers.users);
    const isLoading = useSelector((state: RootState) => state.adminUsers.isLoading);
    const [selectedUser, setSelectedUser] = useState<UserData | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const orders = useSelector((state: RootState) => state.common.orders);

    useEffect(() => {
        dispatch(fetchUsers());
    }, [dispatch]);

    const [sortConfig, setSortConfig] = useState<{
        field: 'lastName' | 'createdAt';
        asc: boolean;
    }>({ field: 'lastName', asc: true });

    const [searchTerm, setSearchTerm] = useState('');

    const filteredUsers = useMemo(() => {
        const sorted = [...users].sort((a, b) => {
            if (sortConfig.field === 'lastName') {
                const res = (a.lastName ?? '').localeCompare(b.lastName ?? '');
                return sortConfig.asc ? res : -res;
            }
            const da = a.createdAt ? new Date(a.createdAt).getTime() : 0;
            const db = b.createdAt ? new Date(b.createdAt).getTime() : 0;
            const res = da - db;
            return sortConfig.asc ? res : -res;
        });

        const term = searchTerm.trim().toLowerCase();
        if (!term) return sorted;

        return sorted.filter(({ lastName, firstName, middleName, phoneNumber }) => {
            const fullName = `${lastName} ${firstName} ${middleName ?? ''}`.toLowerCase();
            const phoneSafe = (phoneNumber ?? '').toLowerCase();
            return fullName.includes(term) || phoneSafe.includes(term);
        });
    }, [users, sortConfig, searchTerm]);

    const toggleSort = (field: 'lastName' | 'createdAt') => {
        setSortConfig((prev) => (prev.field === field ? { field, asc: !prev.asc } : { field, asc: true }));
    };

    const handleStatusChange = async (id: string, newStatus: 'employee' | 'customer') => {
        try {
            await dispatch(updateUserStatus({ id, status: newStatus })).unwrap();
            dispatch(
                showAlert({
                    type: AlertType.Success,
                    message: 'Статус пользователя успешно изменён',
                }),
            );
        } catch (err) {
            dispatch(
                showAlert({
                    type: AlertType.Error,
                    message: typeof err === 'string' ? err : 'Ошибка при изменении статуса. Попробуйте ещё раз',
                }),
            );
        }
    };

    const handleExportClick = async () => {
        if (!filteredUsers.length) {
            dispatch(
                showAlert({
                    type: AlertType.Error,
                    message: 'Нет пользователей для выгрузки',
                }),
            );
            return;
        }

        try {
            await exportUsersToExcel(filteredUsers, orders);
        } catch (err) {
            console.error(err);
            dispatch(
                showAlert({
                    type: AlertType.Error,
                    message: 'Ошибка при выгрузке в Excel. Попробуйте ещё раз',
                }),
            );
        }
    };

    return (
        <>
            <Actions title={`Пользователи - ${users.length}`} />

            <Content>
                <>
                    <div
                        style={{
                            position: 'absolute',
                            top: '-3.5rem',
                            right: '0rem',
                            width: '520px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            gap: '0.8rem',
                        }}
                    >
                        <input
                            type="text"
                            placeholder="Поиск по ФИО или телефону"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                                padding: '4px',
                                width: '100%',
                                maxWidth: '330px',
                                borderRadius: '0.2rem',
                                border: '1px solid rgba(0, 0, 0, 0.3)',
                                textAlign: 'center',
                            }}
                        />
                        <Button mode={'secondary'} title="Выгрузить текущих пользователей в Excel" onClick={handleExportClick} isDisabled={!filteredUsers.length}>
                            Выгрузить в Excel
                        </Button>
                    </div>
                    {isLoading && <Loader />}
                    {!isLoading && (
                        <Card adminTable>
                            <>
                                <table style={{ width: '100%' }}>
                                    <thead>
                                        <tr>
                                            <th onClick={() => toggleSort('lastName')} style={{ cursor: 'pointer' }}>
                                                ФИО {sortConfig.field === 'lastName' ? (sortConfig.asc ? '▲' : '▼') : ''}
                                            </th>
                                            <th>Дата рождения</th>
                                            <th onClick={() => toggleSort('createdAt')} style={{ cursor: 'pointer' }}>
                                                Дата регистрации
                                                {sortConfig.field === 'createdAt' ? (sortConfig.asc ? '▲' : '▼') : ''}
                                            </th>
                                            <th>Телефон</th>
                                            <th>e-mail</th>
                                            <th>Уровень</th>
                                            <th>Статус</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {users.length === 0 && (
                                            <tr>
                                                <td colSpan={8}>
                                                    <Placeholder text={NO_USERS} />
                                                </td>
                                            </tr>
                                        )}

                                        {filteredUsers.map((user) => (
                                            <User
                                                key={user.id}
                                                user={user}
                                                id={user.id}
                                                lastName={user.lastName}
                                                firstName={user.firstName}
                                                middleName={user.middleName}
                                                dateOfBirth={user.dateOfBirth}
                                                createdAt={user.createdAt}
                                                phoneNumber={user.phoneNumber}
                                                email={user.email}
                                                emailStatus={user.emailStatus}
                                                card={user.card}
                                                status={user.status}
                                                highlightTerm={searchTerm}
                                                onStatusChange={handleStatusChange}
                                                onRowClick={(u) => {
                                                    setSelectedUser(u);
                                                    setIsModalOpen(true);
                                                }}
                                            />
                                        ))}
                                    </tbody>
                                </table>
                            </>
                        </Card>
                    )}
                    {isModalOpen && selectedUser && (
                        <div className={styles.modalOverlay} onClick={() => setIsModalOpen(false)}>
                            <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                                <div className={styles.modalHeader}>
                                    <h2>Информация о пользователе</h2>
                                    <button className={styles.modalClose} onClick={() => setIsModalOpen(false)}>
                                        ✕
                                    </button>
                                </div>
                                <div className={styles.user}>
                                    <div>ФИО: {`${selectedUser.lastName} ${selectedUser.firstName} ${selectedUser.middleName ?? ''}`}</div>

                                    <div>
                                        Дата рождения:&nbsp;
                                        {selectedUser.dateOfBirth?.date || '-'}
                                        {selectedUser.dateOfBirth?.updatedAt && (
                                            <span> от {new Date(selectedUser.dateOfBirth.updatedAt).toLocaleDateString('ru-RU')}</span>
                                        )}
                                    </div>

                                    <div>Телефон: {selectedUser.phoneNumber || '-'}</div>

                                    <div>
                                        e-mail: {selectedUser.email || '-'} ({selectedUser.emailStatus ? 'подтверждён' : 'не подтверждён'})
                                    </div>

                                    <div>Адрес: {selectedUser.address?.raw || '-'}</div>

                                    <div>Номер карты: {selectedUser.card?.cardNumber || '-'}</div>
                                    <div>Уровень карты: {getTierName(selectedUser.card?.level)}</div>
                                    <div>Скидка: {selectedUser.card ? `${selectedUser.card.discount}%` : '-'}</div>
                                    <div>Баланс: {selectedUser.card?.balance ?? '-'}</div>

                                    <div>
                                        Последняя авторизация:&nbsp;
                                        {selectedUser.lastAuthDate ? new Date(selectedUser.lastAuthDate).toLocaleString('ru-RU').replace(',', '') : '-'}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            </Content>
        </>
    );
};

export default UsersPage;
